'use client'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faApple,
  faGoogle,
  faMicrosoft,
  type IconDefinition,
} from '@fortawesome/free-brands-svg-icons'
import clsx from 'clsx'
import type { SocialProvider } from '@/lib/apis/types'
import {
  enabledSocialProviders,
  SOCIAL_PROVIDER_ORDER,
  socialProviderLabel,
} from '@/lib/auth/social-auth'
import Spinner from '@/shared/components/Spinner'

const PROVIDER_ICONS: Record<SocialProvider, IconDefinition> = {
  APPLE: faApple,
  GOOGLE: faGoogle,
  MICROSOFT: faMicrosoft,
}

type Props = {
  onSelect: (provider: SocialProvider) => Promise<unknown> | unknown
  providers?: readonly SocialProvider[]
  pendingProvider?: SocialProvider | null
  disabled?: boolean
  variant?: 'page' | 'modal' | 'quiz'
  action?: 'continue' | 'sign-in' | 'link' | 'verify'
  showDivider?: boolean
}

export default function SocialProviderChooser({
  onSelect,
  providers,
  pendingProvider,
  disabled = false,
  variant = 'page',
  action = 'continue',
  showDivider = true,
}: Props) {
  const enabled = enabledSocialProviders()
  const requested = providers ?? SOCIAL_PROVIDER_ORDER
  const visibleProviders = SOCIAL_PROVIDER_ORDER.filter(
    (provider) => enabled.includes(provider) && requested.includes(provider),
  )

  if (visibleProviders.length === 0) return null

  const actionLabel = {
    continue: 'Continue with',
    'sign-in': 'Sign in with',
    link: 'Link',
    verify: 'Verify with',
  }[action]

  return (
    <div className="w-full">
      {showDivider && (
        <div className="relative my-5 flex items-center justify-center">
          <div className="absolute inset-x-0 border-t border-[#E1E1E5]" />
          <span
            className={clsx(
              'relative px-3 text-[12px] leading-4 text-[#82828B]',
              'bg-white',
            )}
          >
            or
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {visibleProviders.map((provider) => {
          const loading = pendingProvider === provider
          return (
            <button
              aria-busy={loading}
              className={clsx(
                'relative flex h-[50px] w-full items-center justify-center rounded-full border border-[#D7D7DC] bg-white px-5 text-[14px] font-semibold leading-[18px] text-[#0B0B0F] transition-colors hover:bg-[#F4F4F6] disabled:cursor-not-allowed disabled:opacity-55',
                variant === 'quiz' && 'rounded-[14px]',
              )}
              disabled={disabled || Boolean(pendingProvider)}
              key={provider}
              onClick={() => void onSelect(provider)}
              type="button"
            >
              {loading ? (
                <Spinner circleColor="gray" />
              ) : (
                <>
                  <FontAwesomeIcon
                    aria-hidden="true"
                    className="absolute left-5 h-[18px] w-[18px]"
                    icon={PROVIDER_ICONS[provider]}
                  />
                  {actionLabel} {socialProviderLabel(provider)}
                </>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
